<template>
  <div
    :class="{'is-collab': isCollabNote}"
    class="aha-slide-notes relative"
    v-if="isBackstage || !isPresenting"
  >
    <textarea
      ref="textarea"
      class="notes-input"
      :disabled="isDisableNote"
      placeholder="Type your note for this slide…"
      :value="slideActive.notes"
      @input="handleChange"
      @click="handleClick"
      @focus="onFocusSlideNoteInput"
      @blur="onBlurSlideNoteInput"
      v-click-outside="handleClickOutside"
    />
    <span
      v-if="!isBackstage"
      class="absolute bottom-0.25 right-4 text-xs pl-1 text-base-80 bg-base-0 rounded-sm"
    >
      Notes will be shown in
      <a class="text-link--primary underline" @click="openBackstageWindow">
        Backstage
      </a>
      <aha-icon
        name="open-new-window"
        class="text-base-50"
        width="18"
        height="18"
        viewBox="-4 -4 32 32"
      />
    </span>
  </div>
</template>

<script>

import { mapState, mapActions, mapGetters } from 'vuex'
import { debounce } from 'lodash'
import SlideApi from '@/api/slide'
import EventBus from '@/eventBus'
import utils from '@/util'
import Socket from '@/socket'
import config from '@/config'
import constants from '@/constant'

export default {
  props: {
    initialContent: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      showTooltip: false,
      allowShowSlideType: [
        'wordCloud',
        'imageChoice',
        'multipleChoice',
        'question',
        'leaderboard',
        'spinnerWheel',
      ],
    }
  },
  computed: {
    ...mapGetters(['isBackstage', 'isPresenting']),
    ...mapState({
      slideActive: (state) => state.slideActive,
      isDisableEditSlide: (state) => state.isDisableEditSlide,
      presentation: (state) => state.presentation,
      presentationEditors: (state) => state.presentationEditors,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
      currentUser: (state) => state.currentUser,
    }),
    isEURegion() {
      return config.region === 'eu'
    },
    slideTypeInPPTAddIn() {
      if (this.slideActive && ['spinnerWheel'].includes(this.slideActive.type)) {
        return true
      }
      return false
    },
    slideTypesAllowToolbar() {
      return !this.allowShowSlideType.includes(this.slideActive.type) &&
          !(this.slideActive.type === 'pickAnswer' && this.slideActive.slideType === null) &&
          !(this.slideActive.type === 'pickAnswer' && this.slideActive.slideType === 'imageChoice') &&
          !this.slideTypeInPPTAddIn
    },
    isToolbarVisible() {
      return !this.isBackstage && !this.isDisableEditSlide &&
          (this.slideTypesAllowToolbar || this.isEURegion)
      && this.slideActive.type === 'freestyle'
    },
    isABTestCollabTeamEdit() {
      if (this.presentation.accountId &&
        !this.presentation.hasSharedUser && this.presentation.accountId % 2 === 0) {
        return true
      }
      return false
    },
    isDisableNote() {
      if (!this.isABTestCollabTeamEdit) {
        return this.isDisableEditPresentation
      }
      return this.isDisableEditSlide
    },
    showAddInPPT() {
      if (this.$store.state.sharePresentation) {
        return false
      }
      if (this.$store.state.workingMode === constants.workspace.TEAM) {
        return false
      }
      let isShowButtonWithCollab = !this.isDisableEditPresentation
      if (this.isABTestCollabTeamEdit) {
        isShowButtonWithCollab = this.showButtonWithCollab(isShowButtonWithCollab)
      }

      return config.region !== 'eu' && !this.isBackstage && isShowButtonWithCollab &&
          (this.allowShowSlideType.includes(this.slideActive.type) ||
              (this.slideActive.type === 'pickAnswer' && this.slideActive.slideType === null) ||
              (this.slideActive.type === 'pickAnswer' && this.slideActive.slideType === 'imageChoice')
          )
    },
    showNoteCanEditThisSlide() {
      return this.presentationEditors.length > 1 ||
          (this.presentationEditors.length === 1 &&
              this.presentationEditors[0].id !== this.presentation.takenOverBy)
    },
    isCollabNote() {
      return this.isABTestCollabTeamEdit && this.showNoteCanEditThisSlide && (this.showAddInPPT || this.isToolbarVisible)
    },
  },
  mounted() {
    EventBus.$on('slide-notes:focus-input', () => {
      if (this.slideActive.notes) {
        if (this.$refs.textarea) this.$refs.textarea.focus()
      }
    })
  },

  destroyed() {
    EventBus.$off('slide-notes:focus-input')
  },
  methods: {
    ...mapActions(['updateSlideActiveAction']),
    showButtonWithCollab(isShowButtonWithCollab) {
      const slideActive = this.presentation.slides.find((slide) => slide.id === this.slideActive.id)
      if (slideActive) {
        const { usersOnline } = slideActive
        if (usersOnline) {
          const currentUserSlide = usersOnline.find((userInfo) => userInfo.user.id === this.currentUser.id)
          if (currentUserSlide) {
            return currentUserSlide.isCanEdit
          }
        }
      }
      return isShowButtonWithCollab
    },
    handleClick() {
      // https://floating-vue.starpad.dev/legacy/v2/#directive
      if (!this.slideActive.notes) {
        // this.showTooltip = true
      }
      utils.gaTrackEvent('Presentation Editor', 'Slide Notes > Click', this.slideActive.id)
    },
    handleChange: debounce(async function (evt) {
      if (this.showTooltip) this.showTooltip = false

      const { value: notes } = evt.target
      this.updateSlideActiveAction({
        notes,
      })

      // set sync presentation
      this.$store.dispatch('setSyncPresentation', 1)
      try {
        await SlideApi.patchSlideById(this.slideActive.id, {
          socketId: Socket.getSocketId(),
          notes,
        })
        this.$store.dispatch('setSyncPresentation', 2)
      } catch (e) {
        this.$store.dispatch('setSyncPresentation', 3)
      }
    }, 300),
    handleClickOutside() {
      if (this.showTooltip) this.showTooltip = false
      this.$store.state.slideActive.isFocusingSlideNoteInput = false
    },
    onFocusSlideNoteInput() {
      this.$store.state.slideActive.isFocusingSlideNoteInput = true
    },
    onBlurSlideNoteInput() {
      this.$store.state.slideActive.isFocusingSlideNoteInput = false
    },
    openBackstageWindow() {
      EventBus.$emit('present-with-backstage')
    },
  },
}
</script>

<style lang="scss">
// no scoped to target tooltip

.aha-slide-notes {
  margin: 0 auto;
  flex: 1;
  width: 100%;
  min-height: 80px;
  max-height: 100px;
  margin-top: auto;
  &.is-collab{
    flex: none;
    min-height: 40px;
    max-height: 40px;
    .notes-input{
      padding-top: 0.5rem;
    }
  }

  .notes-input {
    @apply appearance-none;
    @apply px-3 py-2 text-sm w-full border border-base-30;
    @apply focus:outline-none;
    @apply placeholder-base-70;
    border-left: 1px solid transparent;
    border-right: 1px solid transparent;
    line-height: 18px;
    background: #fff;
    resize: none;
    height: 100%;

    &:hover, &:focus {
      border: 1px solid #348EF6;
    }
  }
  .trigger {
    @apply w-full h-full;
  }
}

.tooltip-inner {
    padding: 10px;
    font-size: 14px;
    line-height: 18px;
  }

.slide-notes-tooltip {
  width: 300px;

  hr {
    margin: 6px 0;
    color: rgba(255, 255, 255, 0.1);
  }

  .tooltip-arrow {
    left: 0 !important;
  }

  .slide-notes-badge {
    background: #525F7F;
    border-radius: 17px;
    padding: 3px 5px;
    font-weight: bold;
    font-size: 8px;
    text-transform: uppercase;
  }
}
</style>
